import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "./api";
import { ArticlePreview } from "./ArticlePreview";
import { MediaPanel } from "./MediaPanel";
import { MediaLibraryPage } from "./MediaLibraryPage";
import { HomepagePage } from './HomepagePage';
import { WysiwygEditor } from "./WysiwygEditor";
import { siteNames, siteOrigins } from "./types";
import type { Admin, ArticleDetail, ArticleDocument, ArticleListItem, ArticlePayload, Category, ContentType, MediaAsset, PublicationJob, SiteId } from "./types";

const statusLabels = { DRAFT: "Черновик", PUBLISHED: "Опубликовано", ARCHIVED: "Архив" } as const;
function lastLegalSite(): SiteId {
  try { const site = sessionStorage.getItem('velncms-legal-site'); if (site && Object.hasOwn(siteNames, site)) return site as SiteId; } catch { /* Storage can be unavailable. */ }
  return 'velnpay';
}
function rememberLegalSite(site: SiteId) {
  try { sessionStorage.setItem('velncms-legal-site', site); } catch { /* Filtering works without storage. */ }
}

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("ru-RU", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function slugify(value: string) {
  const transliteration: Record<string, string> = { а:"a",б:"b",в:"v",г:"g",д:"d",е:"e",ё:"e",ж:"zh",з:"z",и:"i",й:"y",к:"k",л:"l",м:"m",н:"n",о:"o",п:"p",р:"r",с:"s",т:"t",у:"u",ф:"f",х:"h",ц:"c",ч:"ch",ш:"sh",щ:"sch",ъ:"",ы:"y",ь:"",э:"e",ю:"yu",я:"ya" };
  return value.toLowerCase().split("").map((letter) => transliteration[letter] ?? letter).join("").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 100);
}

function usePath() {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  const navigate = useCallback((next: string) => {
    window.history.pushState({}, "", next);
    setPath(next);
  }, []);
  return { path, navigate };
}

function LoginPage({ onLogin }: { onLogin: (admin: Admin) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      onLogin(await api.login(username, password));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Не удалось войти");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-card" aria-labelledby="login-title">
        <div className="login-brand"><span className="brand-mark">V</span><span>VelnCMS</span></div>
        <p className="eyebrow">Редакционная система VelnPay</p>
        <h1 id="login-title">Вход в панель</h1>
        <p className="login-intro">Используйте учётную запись администратора. Публичной регистрации нет.</p>
        <form onSubmit={submit}>
          <label>Логин<input autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} minLength={3} required /></label>
          <label>Пароль<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required /></label>
          {error ? <div className="form-error" role="alert">{error}</div> : null}
          <button className="primary-button" disabled={loading} type="submit">{loading ? "Проверяем…" : "Войти"}</button>
        </form>
        <small>Доступ защищён серверной сессией</small>
      </section>
    </main>
  );
}

function Shell({ admin, path, navigate, onLogout, children }: { admin: Admin; path: string; navigate: (path: string) => void; onLogout: () => void; children: React.ReactNode }) {
  const articleEditor = path === "/articles/new" || /^\/articles\/[^/]+$/.test(path);
  const legalEditor = /^\/legal\/[^/]+$/.test(path);
  const sectionTitle = path === '/homepage' ? 'Карусель главной' : legalEditor ? "Редактор юридического документа" : articleEditor ? "Редактор статьи" : path === "/legal" ? "Юридические документы" : path === "/media" ? "Медиатека" : "Статьи";
  return (
    <div className="cms-shell">
      <aside className="sidebar">
        <button className="sidebar-brand" onClick={() => navigate("/articles")}><span className="brand-mark">V</span><span><b>VelnCMS</b><small>Редакция</small></span></button>
        <nav aria-label="Разделы CMS">
          <button className={path === '/homepage' ? 'active' : ''} onClick={() => navigate('/homepage')}><span>▧</span>Карусель главной</button>
          <button className={path.startsWith("/articles") ? "active" : ""} onClick={() => navigate("/articles")}><span>▤</span>Статьи</button>
          <button className={path.startsWith("/legal") ? "active" : ""} onClick={() => navigate("/legal")}><span>§</span>Юридические документы</button>
          <button className={path === "/media" ? "active" : ""} onClick={() => navigate("/media")}><span>▧</span>Медиатека</button>
          <button disabled title="Будет доступно в следующей версии"><span>⚙</span>Настройки <small>Скоро</small></button>
        </nav>
        <div className="sidebar-foot"><span>VelnCMS v0.1</span><small>Локальная версия</small></div>
      </aside>
      <div className="workspace">
        <header className="topbar">
          <div><p className="eyebrow">VelnPay · Управление контентом</p><h1>{sectionTitle}</h1></div>
          <div className="topbar-actions">
            {path === "/articles" ? <button className="primary-button compact" onClick={() => navigate("/articles/new")}>＋ Создать статью</button> : null}
            <div className="admin-chip"><span>{admin.username.slice(0, 1).toUpperCase()}</span><div><b>{admin.username}</b><small>Администратор</small></div></div>
            <button className="quiet-button" onClick={onLogout}>Выйти</button>
          </div>
        </header>
        <main className="workspace-content">{children}</main>
      </div>
    </div>
  );
}

function ArticlesPage({ navigate, contentType = "ARTICLE" }: { navigate: (path: string) => void; contentType?: ContentType }) {
  const legal = contentType === "LEGAL";
  const [items, setItems] = useState<ArticleListItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState<SiteId | "">(() => legal ? lastLegalSite() : "");
  const loadSequence = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true);
    setError("");
    try {
      const [result, categoryItems] = await Promise.all([api.articles(search, filter, contentType, siteFilter), api.categories()]);
      if (sequence !== loadSequence.current) return;
      setItems(result.items);
      setCategories(categoryItems);
    } catch (reason) {
      if (sequence === loadSequence.current) setError(reason instanceof Error ? reason.message : "Не удалось загрузить статьи");
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [search, filter, contentType, siteFilter]);

  useEffect(() => {
    const timer = window.setTimeout(load, 180);
    return () => window.clearTimeout(timer);
  }, [load]);

  const categoryMap = useMemo(() => new Map(categories.map((category) => [category.id, category.title])), [categories]);

  async function remove(item: ArticleListItem) {
    if (!window.confirm(`Удалить черновик «${item.title}»? Это действие нельзя отменить.`)) return;
    try {
      await api.deleteArticle(item.id, item.version);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Не удалось удалить статью");
    }
  }

  return (
    <section className="articles-section">
      <div className="section-heading"><div><h2>{legal ? "Юридические документы" : "Редакционные материалы"}</h2><p>{legal ? "Системные шаблоны, редакции и публикации документов сайта." : "Черновики и подготовленные публикации игрового журнала."}</p></div></div>
      <div className={'article-tools' + (legal ? ' legal-tools' : '')}>
        <label className="search-field"><span>⌕</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Поиск по названию или slug" aria-label="Поиск статей" /></label>
        <label><span className={legal ? '' : 'visually-hidden'}>{legal ? 'Платформа документов' : 'Фильтр по сайту'}</span><select aria-label={legal ? 'Платформа документов' : 'Фильтр по сайту'} value={siteFilter} onChange={(event) => { const site = event.target.value as SiteId | ''; if (site === siteFilter) return; ++loadSequence.current; setLoading(true); setSiteFilter(site); if (legal && site) rememberLegalSite(site); }}>{!legal ? <option value="">Все сайты</option> : null}{(Object.keys(siteNames) as SiteId[]).map((site) => <option key={site} value={site}>{siteNames[site]} — {siteOrigins[site].replace('https://', '')}</option>)}</select></label>
        <label><span className="visually-hidden">Фильтр по статусу</span><select value={filter} onChange={(event) => setFilter(event.target.value)}><option value="">Все статусы</option><option value="DRAFT">Черновики</option><option value="PUBLISHED">Опубликовано</option><option value="ARCHIVED">Архив</option></select></label>
      </div>
      {error ? <div className="notice error" role="alert">{error}</div> : null}
      {loading ? <div className="loading-state">Загружаем материалы…</div> : items.length ? (
        <div className="articles-table-wrap">
          <table className="articles-table">
            <thead><tr><th>Название</th><th>Сайт</th><th>Категория</th><th>Статус</th><th>Изменено</th><th>Опубликовано</th><th aria-label="Действия" /></tr></thead>
            <tbody>{items.map((item) => (
              <tr key={item.id}>
                <td><button className="article-title-link" onClick={() => navigate(`/${legal ? "legal" : "articles"}/${item.id}`)}>{item.title}</button><small>/{legal ? "legal/" : ""}{item.slug}{item.status === "PUBLISHED" && item.hasUnpublishedChanges ? " · есть неопубликованные изменения" : ""}</small></td>
                <td>{siteNames[item.siteId]}</td>
                <td>{categoryMap.get(item.categoryId) ?? item.categoryId}</td>
                <td><span className={`status-badge ${item.status.toLowerCase()}`}>{statusLabels[item.status]}</span></td>
                <td>{formatDate(item.updatedAt)}</td><td>{formatDate(item.publishedAt)}</td>
                <td><div className="row-actions"><button onClick={() => navigate(`/${legal ? "legal" : "articles"}/${item.id}`)}>Открыть</button>{!legal && item.status === "DRAFT" ? <button className="danger-link" onClick={() => remove(item)}>Удалить</button> : null}</div></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state"><span className="empty-icon">▤</span><h3>{search || filter || siteFilter ? "Ничего не найдено" : legal ? "Юридические документы не созданы" : "Публикаций пока нет"}</h3><p>{search || filter || siteFilter ? "Измените запрос или выбранный статус." : legal ? "Примените актуальную миграцию базы данных." : "Создайте первый черновик — он сохранится в локальной базе VelnCMS."}</p>{!legal && !search && !filter && !siteFilter ? <button className="primary-button" onClick={() => navigate("/articles/new")}>Создать статью</button> : null}</div>
      )}
    </section>
  );
}

const emptyDocument: ArticleDocument = { version: 1, blocks: [{ type: "paragraph", content: [] }] };

function EditorPage({ id, navigate, contentType = "ARTICLE" }: { id?: string; navigate: (path: string) => void; contentType?: ContentType }) {
  const legal = contentType === "LEGAL";
  const [article, setArticle] = useState<ArticleDetail | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [form, setForm] = useState<ArticlePayload>({ siteId: "velnpay", title: "", slug: "", excerpt: "", categoryId: legal ? "legal" : "service", seoTitle: "", seoDescription: "", cover: null, content: emptyDocument, contentType, legalReviewConfirmed: false });
  const [mediaAssets, setMediaAssets] = useState<MediaAsset[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [slugTouched, setSlugTouched] = useState(Boolean(id));
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [confirmingReview, setConfirmingReview] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [switchingSite, setSwitchingSite] = useState(false);
  const [publication, setPublication] = useState<PublicationJob | null>(null);
  const [message, setMessage] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([api.categories(), id ? api.article(id) : Promise.resolve(null)]).then(([categoryItems, loaded]) => {
      if (!active) return;
      setCategories(categoryItems);
      if (loaded) {
        if (loaded.contentType !== contentType) throw new Error("Материал открыт не в своём разделе CMS");
        setArticle(loaded);
        if (legal) rememberLegalSite(loaded.siteId);
        setForm({ siteId: loaded.siteId, title: loaded.title, slug: loaded.slug, excerpt: loaded.excerpt, categoryId: loaded.categoryId, seoTitle: loaded.seoTitle, seoDescription: loaded.seoDescription, cover: loaded.cover, content: loaded.content, contentType: loaded.contentType, legalReviewConfirmed: false });
        setMediaAssets(loaded.media.map((asset) => ({ ...asset, byteSize: asset.byteSize ?? 0, sha256: "", originalName: asset.src.split("/").at(-1) ?? asset.id, articleId: loaded.id, createdAt: loaded.updatedAt })));
        api.latestPublication(loaded.id).then(setPublication).catch(() => undefined);
      }
    }).catch((reason) => setMessage({ kind: "error", text: reason instanceof Error ? reason.message : "Не удалось открыть статью" })).finally(() => setLoading(false));
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => { if (dirty) event.preventDefault(); };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);

  useEffect(() => {
    if (!publication || !["QUEUED", "BUILDING", "DEPLOYING"].includes(publication.status)) return;
    const timer = window.setInterval(async () => {
      try {
        const current = await api.publication(publication.id);
        setPublication(current);
        if (current.status === "SUCCEEDED" && article) setArticle(await api.article(article.id));
      } catch { /* Следующая попытка обновит статус. */ }
    }, 2500);
    return () => window.clearInterval(timer);
  }, [publication, article]);

  function change<K extends keyof ArticlePayload>(key: K, value: ArticlePayload[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setDirty(true);
  }

  function changeTitle(value: string) {
    setForm((current) => ({ ...current, title: value, slug: slugTouched ? current.slug : slugify(value), seoTitle: current.seoTitle || value.slice(0, 70) }));
    setDirty(true);
  }

  function safeNavigate(path: string) {
    if (!dirty || window.confirm("Есть несохранённые изменения. Покинуть редактор?")) navigate(path);
  }

  async function selectLegalSite(site: SiteId) {
    if (site === form.siteId || !article) return;
    if (dirty && !window.confirm('Есть несохранённые изменения. Перейти к документу другой платформы?')) return;
    setSwitchingSite(true);
    try {
      const documents = await api.articles('', '', 'LEGAL', site);
      const target = documents.items.find(item => item.slug === article.slug);
      if (!target) throw new Error('Комплект документов этой платформы не создан. Примените миграцию CMS 0010.');
      rememberLegalSite(site);
      navigate('/legal/' + target.id);
    } catch (reason) { setMessage({ kind: 'error', text: reason instanceof Error ? reason.message : 'Не удалось сменить платформу' }); }
    finally { setSwitchingSite(false); }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setMessage(null);
    try {
      const saved = article
        ? await api.updateArticle(article.id, { ...form, version: article.version })
        : await api.createArticle(form);
      setArticle(saved);
      setForm({ siteId: saved.siteId, title: saved.title, slug: saved.slug, excerpt: saved.excerpt, categoryId: saved.categoryId, seoTitle: saved.seoTitle, seoDescription: saved.seoDescription, cover: saved.cover, content: saved.content, contentType: saved.contentType, legalReviewConfirmed: false });
      setDirty(false);
      setSlugTouched(true);
      setMessage({ kind: "success", text: `Редакция сохранена · версия ${saved.revisionNumber}` });
      if (!id) window.history.replaceState({}, "", `/${legal ? "legal" : "articles"}/${saved.id}`);
    } catch (reason) {
      if (reason instanceof ApiError && reason.code === "version_conflict") setMessage({ kind: "error", text: "Черновик изменён в другой вкладке. Обновите страницу перед повторным сохранением." });
      else setMessage({ kind: "error", text: reason instanceof Error ? reason.message : "Не удалось сохранить черновик" });
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    if (!article || dirty || publishing) return;
    setPublishing(true);
    setMessage(null);
    try {
      const job = await api.publishArticle(article.id);
      setPublication(job);
      setMessage({ kind: "success", text: "Snapshot зафиксирован и передан build runner" });
    } catch (reason) {
      setMessage({ kind: "error", text: reason instanceof Error ? reason.message : "Не удалось запустить публикацию" });
    } finally {
      setPublishing(false);
    }
  }

  async function confirmReview() {
    if (!article || dirty || confirmingReview) return;
    setConfirmingReview(true);
    setMessage(null);
    try {
      const confirmed = await api.confirmLegalReview(article.id, article.version, article.revisionId);
      setArticle(confirmed);
      setMessage({ kind: "success", text: `Юридическая проверка подтверждена для редакции ${confirmed.revisionNumber}` });
    } catch (reason) {
      setMessage({ kind: "error", text: reason instanceof Error ? reason.message : "Не удалось подтвердить юридическую проверку" });
    } finally {
      setConfirmingReview(false);
    }
  }

  if (loading) return <div className="loading-state">Открываем редактор…</div>;

  const requiredPlaceholders = JSON.stringify(form.content).match(/\[\[REQUIRED:[a-z0-9_.-]+\]\]/g) ?? [];
  const reviewConfirmed = Boolean(article?.legalReviewConfirmed) && !dirty;
  const legalPublicationBlocked = legal && (requiredPlaceholders.length > 0 || !reviewConfirmed);
  const editorCategories = categories.filter((category) => legal ? category.id === "legal" : category.id !== "legal");

  return (
    <form className="editor" onSubmit={save}>
      <div className="editor-toolbar"><button type="button" className="back-button" onClick={() => safeNavigate(legal ? "/legal" : "/articles")}>← К списку</button><div className="editor-toolbar-actions"><button type="button" className="secondary-button compact" onClick={() => setPreviewOpen(true)}>Предпросмотр</button><div className="save-state">{dirty ? <span className="unsaved-dot" /> : null}{dirty ? "Есть несохранённые изменения" : article ? `Сохранено · редакция ${article.revisionNumber}` : "Новый черновик"}</div></div></div>
      {message ? <div className={`notice ${message.kind}`} role={message.kind === "error" ? "alert" : "status"}>{message.text}</div> : null}
      <div className="editor-layout">
        <div className="editor-main">
          <section className="panel editor-fields">
            <label>Заголовок<input className="title-input" value={form.title} onChange={(event) => changeTitle(event.target.value)} maxLength={160} placeholder="Название материала" required /></label>
            <label>Slug<div className="slug-field"><span>{siteOrigins[form.siteId].replace("https://", "")}/{legal ? "legal" : "blog"}/</span><input value={form.slug} onChange={(event) => { setSlugTouched(true); change("slug", event.target.value.toLowerCase()); }} maxLength={100} placeholder="article-slug" required readOnly={legal} /></div></label>
            <label>Краткое описание<textarea rows={3} value={form.excerpt} onChange={(event) => change("excerpt", event.target.value)} maxLength={320} placeholder="Лид для карточки и страницы статьи" required /><small>{form.excerpt.length}/320</small></label>
          </section>
          <section className="panel content-editor">
            <div className="panel-heading"><div><h2>Содержание</h2><p>Единое редакционное полотно · ArticleDocument v1</p></div></div>
            <WysiwygEditor
              value={form.content}
              media={mediaAssets}
              articleId={article?.id}
              onChange={(content) => change("content", content)}
              onMediaChange={setMediaAssets}
            />
          </section>
          <MediaPanel articleId={article?.id} cover={form.cover} onAssetsChange={(assets) => setMediaAssets((current) => [...new Map([...current, ...assets].map((asset) => [asset.id, asset])).values()])} onCoverChange={(cover) => change("cover", cover)} />
        </div>
        <aside className="editor-sidebar">
          <section className="panel publish-panel">
            <div className="panel-heading"><div><h2>Параметры</h2><p>{article?.status === "PUBLISHED" ? article.hasUnpublishedChanges ? "Есть неопубликованная редакция" : "Опубликованный материал" : "Черновик"}</p></div><span className={`status-badge ${(article?.status ?? "DRAFT").toLowerCase()}`}>{statusLabels[article?.status ?? "DRAFT"]}</span></div>
            <label>Опубликовать на сайте<select aria-label="Опубликовать на сайте" value={form.siteId} onChange={(event) => legal ? void selectLegalSite(event.target.value as SiteId) : change('siteId', event.target.value as SiteId)} disabled={legal ? saving || publishing || confirmingReview || switchingSite : Boolean(article)}>{(Object.keys(siteNames) as SiteId[]).map((site) => <option key={site} value={site}>{siteNames[site]} — {siteOrigins[site].replace('https://', '')}</option>)}</select>{legal ? <small>Выбор открывает отдельную редакцию этого документа для выбранной платформы. Текст и подтверждение проверки не копируются.</small> : article ? <small>Сайт закреплён за статьёй. Для переноса создайте новый черновик.</small> : null}</label>
            <label>Категория<select value={form.categoryId} onChange={(event) => change("categoryId", event.target.value)} disabled={legal}>{editorCategories.map((category) => <option key={category.id} value={category.id}>{category.title}</option>)}</select></label>
            {legal ? <div className={`legal-publication-guard ${legalPublicationBlocked ? "blocked" : "ready"}`}><b>{requiredPlaceholders.length ? `Осталось обязательных полей: ${requiredPlaceholders.length}` : reviewConfirmed ? `Редакция ${article?.revisionNumber} юридически проверена` : "Требуется проверка сохранённой редакции"}</b><small>{requiredPlaceholders.length ? [...new Set(requiredPlaceholders)].join(", ") : dirty ? "Сначала сохраните изменения — прежнее подтверждение к ним не применяется." : "Подтверждение относится только к текущей неизменной редакции."}</small><button className="secondary-button compact" type="button" disabled={!article || dirty || requiredPlaceholders.length > 0 || reviewConfirmed || confirmingReview} onClick={confirmReview}>{confirmingReview ? "Подтверждаем…" : reviewConfirmed ? "Проверка подтверждена" : "Подтвердить проверку редакции"}</button></div> : null}
            <div className="date-grid"><span><small>Создано</small>{article ? formatDate(article.createdAt) : "После сохранения"}</span><span><small>Изменено</small>{article ? formatDate(article.updatedAt) : "—"}</span></div>
            <button className="primary-button save-button" disabled={saving || !dirty && Boolean(article)} type="submit">{saving ? "Сохраняем…" : article ? "Сохранить изменения" : "Сохранить черновик"}</button>
            <button className="secondary-button publish-button" disabled={!article || dirty || publishing || legalPublicationBlocked || Boolean(publication && ["QUEUED", "BUILDING", "DEPLOYING"].includes(publication.status))} type="button" onClick={publish}>{publishing ? "Передаём…" : article?.status === "PUBLISHED" ? "Опубликовать изменения" : "Опубликовать"}</button>
            {!article ? <small className="publish-hint">Сначала сохраните черновик.</small> : dirty ? <small className="publish-hint">Сохраните изменения перед публикацией.</small> : legalPublicationBlocked ? <small className="publish-hint">Публикация заблокирована до заполнения обязательных данных и подтверждения проверки.</small> : null}
            {publication ? <div className={`publication-state ${publication.status.toLowerCase()}`}><b>{publication.status === "QUEUED" ? "В очереди на сборку" : publication.status === "BUILDING" ? "Собираем сайт" : publication.status === "DEPLOYING" ? `Обновляем ${siteOrigins[form.siteId].replace("https://", "")}` : publication.status === "SUCCEEDED" ? "Опубликовано" : "Ошибка публикации"}</b><small>{publication.error ?? (publication.publicUrl ? publication.publicUrl : `Задание ${publication.id.slice(0, 8)}`)}</small></div> : null}
          </section>
          <section className="panel seo-panel"><div className="panel-heading"><div><h2>SEO</h2><p>Метаданные будущей публикации</p></div></div><label>Meta title<input value={form.seoTitle} onChange={(event) => change("seoTitle", event.target.value)} maxLength={70} required /><small>{form.seoTitle.length}/70</small></label><label>Meta description<textarea rows={4} value={form.seoDescription} onChange={(event) => change("seoDescription", event.target.value)} maxLength={180} required /><small>{form.seoDescription.length}/180</small></label></section>
        </aside>
      </div>
      {previewOpen ? <ArticlePreview article={form} categories={categories} media={mediaAssets} onClose={() => setPreviewOpen(false)} /> : null}
    </form>
  );
}

export function App() {
  const { path, navigate } = usePath();
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.me().then(setAdmin).catch(() => setAdmin(null)).finally(() => setLoading(false));
  }, []);

  async function logout() {
    try { await api.logout(); } finally { setAdmin(null); navigate("/login"); }
  }

  if (loading) return <div className="app-loading"><span className="brand-mark">V</span><p>Запускаем VelnCMS…</p></div>;
  if (!admin) return <LoginPage onLogin={(value) => { setAdmin(value); navigate("/articles"); }} />;

  const articleMatch = path.match(/^\/articles\/([^/]+)$/);
  const legalMatch = path.match(/^\/legal\/([^/]+)$/);
  return (
    <Shell admin={admin} path={path} navigate={navigate} onLogout={logout}>
      {path === '/homepage' ? <HomepagePage /> : path === "/articles/new" ? <EditorPage navigate={navigate} /> : articleMatch ? <EditorPage id={articleMatch[1]} navigate={navigate} /> : legalMatch ? <EditorPage key={legalMatch[1]} id={legalMatch[1]} navigate={navigate} contentType="LEGAL" /> : path === "/legal" ? <ArticlesPage key="legal" navigate={navigate} contentType="LEGAL" /> : path === "/media" ? <MediaLibraryPage /> : <ArticlesPage key="articles" navigate={navigate} />}
    </Shell>
  );
}
